import cantileverThinkingGif from 'assets/cantilever-thinking.gif'
import { Text } from 'designSystem/components/Text'
import {
  formatResultLabel,
  getAgenticResponseStatusText,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticSearchText'
import type {
  AgenticConversationResponseEvent,
  AgenticConversationThoughtEvent,
} from 'features/knowledgeDocumentViewer/types'
import { CircleCheck, CircleDot } from 'lucide-react'
import React, { useEffect, useId, useState } from 'react'

import {
  ExpandedThoughtConnector,
  ExpandedThoughtIcon,
  ExpandedThoughtIconColumn,
  ExpandedThoughtRow,
  ExpandedThoughtText,
  ResultCount,
  ThinkingGif,
  ThinkingStatusRow,
  ThoughtsChevron,
  ThoughtsExpanded,
  ThoughtsSummary,
  ThoughtsToggle,
} from './agenticSearchChatStyles'

function getThoughtSummary({
  isThinking,
  responseEvent,
  thoughts,
}: {
  isThinking: boolean
  responseEvent?: AgenticConversationResponseEvent
  thoughts: AgenticConversationThoughtEvent[]
}): string {
  if (isThinking) {
    return 'Thinking...'
  }
  if (responseEvent) {
    return getAgenticResponseStatusText(responseEvent)
  }
  const latestThought = thoughts[thoughts.length - 1]?.message?.trim()
  if (latestThought) {
    return latestThought
  }
  return 'Thoughts'
}

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => {
      setPrefersReducedMotion(mediaQuery.matches)
    }
    onChange()
    mediaQuery.addEventListener('change', onChange)
    return () => mediaQuery.removeEventListener('change', onChange)
  }, [])

  return prefersReducedMotion
}

function ThinkingIndicator(): React.ReactElement | null {
  const prefersReducedMotion = usePrefersReducedMotion()
  if (prefersReducedMotion) return null
  return (
    <ThinkingGif
      src={cantileverThinkingGif}
      alt=''
      aria-hidden='true'
      draggable={false}
    />
  )
}

function ExpandedThoughtItem({
  children,
  isLast,
  title,
}: {
  children: React.ReactNode
  isLast: boolean
  title?: string
}): React.ReactElement {
  return (
    <ExpandedThoughtRow>
      <ExpandedThoughtIconColumn aria-hidden='true'>
        <ExpandedThoughtIcon>
          {isLast ? <CircleCheck size={12} /> : <CircleDot size={12} />}
        </ExpandedThoughtIcon>
        {isLast ? null : <ExpandedThoughtConnector />}
      </ExpandedThoughtIconColumn>
      <ExpandedThoughtText $isLast={isLast} title={title}>
        {children}
      </ExpandedThoughtText>
    </ExpandedThoughtRow>
  )
}

export function AgenticCollapsibleThoughts({
  isThinking,
  responseEvent,
  thoughts,
}: {
  isThinking: boolean
  responseEvent?: AgenticConversationResponseEvent
  thoughts: AgenticConversationThoughtEvent[]
}): React.ReactElement | null {
  const [isOpen, setIsOpen] = useState(false)
  const contentId = useId()
  const summary = getThoughtSummary({ isThinking, responseEvent, thoughts })
  const canExpand = thoughts.length > 0 || responseEvent !== undefined

  if (!canExpand) {
    if (!isThinking) return null
    return (
      <ThinkingStatusRow aria-live='polite'>
        <ThinkingIndicator />
        <Text as='span' variant='body' color='muted'>
          Thinking...
        </Text>
      </ThinkingStatusRow>
    )
  }

  return (
    <div>
      <ThoughtsToggle
        type='button'
        aria-controls={contentId}
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen((open) => !open)
        }}>
        {isThinking ? <ThinkingIndicator /> : null}
        <ThoughtsSummary>{summary}</ThoughtsSummary>
        {isThinking ? null : (
          <ThoughtsChevron $open={isOpen} aria-hidden='true' size={14} />
        )}
      </ThoughtsToggle>
      {isOpen ? (
        <ThoughtsExpanded id={contentId}>
          {thoughts.map((thought, index) => (
            <ExpandedThoughtItem
              key={thought.id}
              isLast={
                responseEvent === undefined && index === thoughts.length - 1
              }>
              {thought.message}
            </ExpandedThoughtItem>
          ))}
          {responseEvent !== undefined ? (
            <ExpandedThoughtItem
              isLast
              title={responseEvent.metadata.query_plan.elasticsearch_query}>
              Updated search query and found{' '}
              <ResultCount>
                {formatResultLabel(responseEvent.resultCount)}
              </ResultCount>
            </ExpandedThoughtItem>
          ) : null}
        </ThoughtsExpanded>
      ) : null}
    </div>
  )
}
